// Where the Debug: Graph tab draws each node: a band of tracked dates over one lane per
// agent, x by date (Timeline) or by step in the chain (Flow). Cards that would overlap in
// a lane stack in sub-rows. See docs/Y2026-Q4/design-debug-graph.md.
import type { Graph, GraphLink, GraphNode } from "./dependencyGraph";
import { toDay } from "./dependencyGraph";

export type GraphView = "timeline" | "flow";

export const CARD_W = 200;
export const CARD_H = 38;
export const LANE_LABEL_W = 170;
export const AXIS_H = 36;
const ROW_H = 48;
const LANE_PAD = 10;
const BAND_ROW_H = 30;
const BAND_LABEL_W = 160;
const COLUMN_W = CARD_W + 44;
const GAP = 8;
/** Where a card's date sits along it: the dot near its left edge. */
export const DOT_X = 13;
/** The Timeline's reach around today; an item dated outside it sits at the edge, so one
 * far-off expiry cannot stretch the drawing over years. Its card still says its date. */
const WINDOW_BEFORE_DAYS = 90;
const WINDOW_AFTER_DAYS = 365;

export interface Lane {
  agent: string;
  top: number;
  height: number;
}

export interface GraphLayout {
  width: number;
  height: number;
  bandTop: number;
  bandHeight: number;
  lanes: Lane[];
  /** Timeline: the first day drawn, pixels per day, and the undated column's width. */
  startDay: number;
  endDay: number;
  pxPerDay: number;
  gutter: number;
  /** Flow: how many step columns. */
  columns: number;
  /** A card's top-left, by node id. */
  cards: Map<string, { left: number; top: number }>;
  /** A tracked date's diamond centre, by node id. */
  dates: Map<string, { x: number; y: number }>;
  /** The x of a day (Timeline) or of a column's left edge (Flow). */
  xOfDay: (day: number) => number;
  xOfColumn: (column: number) => number;
}

/** The longest chain of waits before each node: a date comes after what works towards
 * it. A loop counts as no wait, and nothing worked out while one was hit is kept, so the
 * result does not depend on which node was asked first. */
const depths = (nodes: GraphNode[], links: GraphLink[]): Map<string, number> => {
  const memo = new Map<string, number>();
  const busy = new Set<string>();
  const before = new Map<string, string[]>();
  let loops = 0;
  for (const l of links) {
    const into = l.kind === "waits" ? l.to : l.kind === "serves" ? l.to : null;
    if (into) before.set(into, [...(before.get(into) ?? []), l.from]);
  }
  const depth = (id: string): number => {
    const known = memo.get(id);
    if (known !== undefined) return known;
    if (busy.has(id)) {
      loops += 1;
      return 0;
    }
    const at = loops;
    busy.add(id);
    const prior = before.get(id) ?? [];
    const d = prior.length ? 1 + Math.max(...prior.map(depth)) : 0;
    busy.delete(id);
    if (loops === at) memo.set(id, d);
    return d;
  };
  for (const n of nodes) depth(n.id);
  return memo;
};

/** Greedy rows: each item takes the first row whose last item ends before it starts. */
const pack = <T extends { left: number }>(list: T[], width: number) => {
  const rowEnds: number[] = [];
  const rows = new Map<T, number>();
  for (const item of [...list].sort((a, b) => a.left - b.left)) {
    let row = rowEnds.findIndex((end) => end + GAP <= item.left);
    if (row === -1) {
      rowEnds.push(0);
      row = rowEnds.length - 1;
    }
    rowEnds[row] = item.left + width;
    rows.set(item, row);
  }
  return { rows, count: Math.max(rowEnds.length, 1) };
};

export const layoutGraph = (
  graph: Graph,
  shown: GraphNode[],
  links: GraphLink[],
  view: GraphView,
  pxPerDay: number,
  agents: string[],
): GraphLayout => {
  const today = toDay(graph.today);
  const inWindow = (day: number) =>
    Math.min(Math.max(day, today - WINDOW_BEFORE_DAYS), today + WINDOW_AFTER_DAYS);
  const days = shown
    .map((n) => n.day)
    .filter((d): d is number => d !== null)
    .map(inWindow);
  const startDay = Math.min(today, ...days) - 5;
  const endDay =
    Math.max(today + 28, ...days) + Math.ceil(Math.max(CARD_W, BAND_LABEL_W) / pxPerDay) + 3;
  const gutter =
    view === "timeline" && shown.some((n) => n.day === null && n.kind !== "date")
      ? CARD_W + 30
      : 0;
  const depth = view === "flow" ? depths(shown, links) : new Map<string, number>();
  const columns = view === "flow" ? Math.max(0, ...depth.values()) + 1 : 0;
  const xOfDay = (day: number) => LANE_LABEL_W + gutter + (day - startDay) * pxPerDay;
  const xOfColumn = (column: number) => LANE_LABEL_W + 24 + column * COLUMN_W;
  // The x a node's date (or step) sits at; undated cards go in the No date column.
  const anchorX = (n: GraphNode): number =>
    view === "flow"
      ? xOfColumn(depth.get(n.id) ?? 0) + DOT_X
      : n.day === null
        ? LANE_LABEL_W + 14 + DOT_X
        : xOfDay(inWindow(n.day));

  const dateNodes = shown
    .filter((n) => n.kind === "date" && (view === "flow" || n.day !== null))
    .map((n) => ({ n, left: anchorX(n) - 8 }));
  const band = pack(dateNodes, BAND_LABEL_W);
  const bandTop = AXIS_H;
  const bandHeight = 18 + band.count * BAND_ROW_H;
  const dates = new Map(
    dateNodes.map((d) => [
      d.n.id,
      { x: d.left + 8, y: bandTop + 20 + (band.rows.get(d) ?? 0) * BAND_ROW_H },
    ]),
  );

  const cards = new Map<string, { left: number; top: number }>();
  const lanes: Lane[] = [];
  let y = bandTop + bandHeight;
  for (const agent of agents) {
    const list = shown
      .filter((n) => n.kind !== "date" && n.agent === agent)
      .map((n) => ({ n, left: anchorX(n) - DOT_X }));
    const { rows, count } = pack(list, CARD_W);
    const height = LANE_PAD * 2 + count * ROW_H - (ROW_H - CARD_H);
    for (const item of list)
      cards.set(item.n.id, {
        left: item.left,
        top: y + LANE_PAD + (rows.get(item) ?? 0) * ROW_H,
      });
    lanes.push({ agent, top: y, height: Math.max(height, 56) });
    y += Math.max(height, 56);
  }

  const width =
    view === "timeline"
      ? xOfDay(endDay) + 20
      : xOfColumn(columns) + 20;
  return {
    width,
    height: y + 4,
    bandTop,
    bandHeight,
    lanes,
    startDay,
    endDay,
    pxPerDay,
    gutter,
    columns,
    cards,
    dates,
    xOfDay,
    xOfColumn,
  };
};
